import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { ArrowRightIcon, TrendingUpIcon, UsersIcon, StarIcon, CalendarIcon } from 'lucide-react';
export function CaseStories() {
  const caseStories = [{
    title: 'SaaS Scale-Up Team Alignment',
    company: 'Growing Tech Company',
    type: 'Executive Retreat',
    people: '28 people',
    duration: '4 days',
    challenge: 'Rapidly growing team needed to align on product roadmap and company vision while building stronger relationships across departments.',
    solution: 'Custom 4-day retreat combining strategic planning sessions with team building activities in a beachfront setting.',
    outcomes: {
      main: '+24% eNPS',
      secondary: '$180k idea pipeline',
      tertiary: '3 new product features'
    },
    testimonial: "Our team came back energized and aligned. The beach setting sparked creativity we couldn't achieve in our office.",
    role: 'VP of People',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Insurance Sales Team Incentive',
    company: 'National Insurance Provider',
    type: 'Sales Incentive',
    people: '120 people',
    duration: '5 days',
    challenge: 'Needed to reward top performers while motivating the entire sales organization for upcoming growth targets.',
    solution: 'Luxury 5-day experience with tiered activities based on performance levels, combined with future-focused training.',
    outcomes: {
      main: '97% satisfaction',
      secondary: '18% sales increase',
      tertiary: '92% retention of top performers'
    },
    testimonial: 'DestaMeet handled everything flawlessly. Our leadership team could focus on content while they managed all logistics.',
    role: 'Sales Director',
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Creative Agency Brand Launch',
    company: 'International Creative Agency',
    type: 'Brand Launch',
    people: '45 people',
    duration: '4 days',
    challenge: 'Launching a major rebrand while ensuring team members from multiple global offices were aligned on messaging and execution.',
    solution: 'Immersive brand experience with interactive workshops, content creation sessions, and strategic planning.',
    outcomes: {
      main: '12 new concepts',
      secondary: '4 major client wins',
      tertiary: '100% brand alignment'
    },
    testimonial: "The change of scenery from boardroom to beach unlocked creative thinking we hadn't accessed before.",
    role: 'Creative Director',
    image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1239">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-1240">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1241">
          <div className="max-w-3xl" data-id="element-1242">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1243">
              Case Stories
            </h1>
            <p className="text-xl text-white/90" data-id="element-1244">
              Real results from organizations that swapped their boardroom for
              the beach.
            </p>
          </div>
        </div>
      </section>

      {/* Case Stories List */}
      <section className="py-16 bg-white" data-id="element-1245">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1246">
          <div className="space-y-12" data-id="element-1247">
            {caseStories.map((story, index) => <Card key={index} className="overflow-hidden" data-id="element-1248">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-1249">
                  <div className="md:order-2" data-id="element-1250">
                    <div className="h-full" data-id="element-1251">
                      <img src={story.image} alt={story.title} className="w-full h-full object-cover" data-id="element-1252" />
                    </div>
                  </div>
                  <div className="p-6" data-id="element-1253">
                    <div className="flex justify-between items-start mb-4" data-id="element-1254">
                      <div data-id="element-1255">
                        <h2 className="text-2xl font-bold text-[#374047]" data-id="element-1256">
                          {story.title}
                        </h2>
                        <p className="text-[#374047]/70" data-id="element-1257">{story.company}</p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-1258">
                        {story.type}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 mb-6" data-id="element-1259">
                      <div className="flex items-center" data-id="element-1260">
                        <UsersIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-1261" />
                        <span className="text-sm" data-id="element-1262">{story.people}</span>
                      </div>
                      <div className="flex items-center" data-id="element-1263">
                        <CalendarIcon className="h-4 w-4 text-[#0099A1] mr-2" data-id="element-1264" />
                        <span className="text-sm" data-id="element-1265">{story.duration}</span>
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-1266">
                      <h3 className="text-lg font-semibold text-[#0099A1] mb-2" data-id="element-1267">
                        Challenge
                      </h3>
                      <p className="text-[#374047]/80" data-id="element-1268">{story.challenge}</p>
                    </div>
                    <div className="mb-6" data-id="element-1269">
                      <h3 className="text-lg font-semibold text-[#0099A1] mb-2" data-id="element-1270">
                        Solution
                      </h3>
                      <p className="text-[#374047]/80" data-id="element-1271">{story.solution}</p>
                    </div>
                    <div className="mb-6" data-id="element-1272">
                      <h3 className="text-lg font-semibold text-[#0099A1] mb-2" data-id="element-1273">
                        Outcomes
                      </h3>
                      <div className="flex flex-wrap gap-3" data-id="element-1274">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#FF6B65]/10 text-[#FF6B65]" data-id="element-1275">
                          <TrendingUpIcon className="h-4 w-4 mr-1" data-id="element-1276" />
                          {story.outcomes.main}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-1277">
                          {story.outcomes.secondary}
                        </span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#F4E8D0] text-[#374047]" data-id="element-1278">
                          {story.outcomes.tertiary}
                        </span>
                      </div>
                    </div>
                    <blockquote className="italic text-[#374047]/80 border-l-4 border-[#0099A1] pl-4 mb-4" data-id="element-1279">
                      "{story.testimonial}"
                      <footer className="mt-2 text-sm text-[#374047]/60" data-id="element-1280">
                        — {story.role}, {story.company}
                      </footer>
                    </blockquote>
                    <Button variant="outline" className="mt-2" data-id="element-1281">
                      Read Full Case Study
                      <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-1282" />
                    </Button>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1283" />
    </div>;
}